/**
 * Groups GraphQL Service
 * Consumes group-related GraphQL queries from resources/core
 */

import { graphqlClient } from '@/services/core/graphqlClient';
import { GROUPS_QUERIES } from '@/services/resources';

export interface GroupDetails {
  group_id: number;
  group_name: string;
  group_type: string;
  group_type_id: number;
  clinic_name: string;
  es_id: number;
  es_name: string;
  number_of_patients: number;
  updated_time_utc: string;
  patient_clinic_timezone: string;
  group_filters?: string;
  is_archive: boolean;
  clinic_id?: number;
}

export interface GroupType {
  id: number;
  group_type: string;
}

export interface GroupPatientList {
  assigned_clinic_id: number;
  patient_list: any[];
}

export class GroupsGraphqlService {
  /**
   * Get group details with filtering
   */
  async getGroupDetails(hasuraToken: string, where?: any) {
    return await graphqlClient.query<{ pep_vw_group_details: GroupDetails[] }>(
      GROUPS_QUERIES.GET_GROUP_DETAILS,
      { where },
      hasuraToken
    );
  }

  /**
   * Get all active groups
   */
  async getAllActiveGroups(hasuraToken: string) {
    return await graphqlClient.query<{ pep_vw_group_details: GroupDetails[] }>(
      GROUPS_QUERIES.GET_ALL_ACTIVE_GROUPS,
      {},
      hasuraToken
    );
  }

  /**
   * Get archived groups
   */
  async getArchivedGroups(hasuraToken: string) {
    return await graphqlClient.query<{ pep_vw_group_details: GroupDetails[] }>(
      GROUPS_QUERIES.GET_ARCHIVED_GROUPS,
      {},
      hasuraToken
    );
  }

  /**
   * Get available group types
   */
  async getGroupTypes(hasuraToken: string) {
    return await graphqlClient.query<{ pep_group_type_mapping: GroupType[] }>(
      GROUPS_QUERIES.GET_GROUP_TYPES,
      {},
      hasuraToken
    );
  }

  /**
   * Get patient list for a specific group
   */
  async getGroupPatientList(hasuraToken: string, groupId: number) {
    return await graphqlClient.query<{ pep_vw_group_patient_list: GroupPatientList[] }>(
      GROUPS_QUERIES.GET_GROUP_PATIENT_LIST,
      { groupId },
      hasuraToken
    );
  }

  /**
   * Check if group name already exists
   */
  async checkGroupName(hasuraToken: string, groupName: string) {
    return await graphqlClient.query<{ pep_vw_group_details: GroupDetails[] }>(
      GROUPS_QUERIES.CHECK_GROUP_NAME,
      { groupName },
      hasuraToken
    );
  }

  /**
   * Get patient group dashboard data
   */
  async getPatientGroupDashboard(hasuraToken: string) {
    return await graphqlClient.query<{ pep_vw_patient_group_dashboard: PatientGroupDashboard[] }>(
      GROUPS_QUERIES.GET_PATIENT_GROUP_DASHBOARD,
      {},
      hasuraToken
    );
  }
}

export interface PatientGroupDashboard {
  group_name: string;
  is_archived: boolean;
  patient_count: number;
  patient_group_id: number;
}

// Export singleton instance
export const groupsGraphqlService = new GroupsGraphqlService();
export default groupsGraphqlService;